import { supabase } from "./supabase-client";

export type OrganizationRole =
  | "owner"
  | "admin"
  | "editor"
  | "staff"
  | "finance"
  | "manager"
  | "viewer"
  | "checkin_staff"
  | "reports_only";

export type OrgPermission =
  | "edit_events"
  | "view_orders"
  | "view_financial"
  | "checkin"
  | "manage_team"
  | "manage_coupons"
  | "send_messages";

export const PERMISSION_CONFIG: Record<OrgPermission, { label: string; description: string }> = {
  edit_events:    { label: "Criar e editar eventos",    description: "Cria, edita, publica e gerencia ingressos e lotes" },
  view_orders:    { label: "Ver pedidos e participantes", description: "Acessa lista de pedidos, participantes e convidados" },
  view_financial: { label: "Acessar financeiro",        description: "Vê receita, repasses e dados financeiros do evento" },
  checkin:        { label: "Realizar check-in",          description: "Faz leitura de ingressos e check-in no evento" },
  manage_team:    { label: "Gerenciar equipe",           description: "Convida, remove e altera cargos de membros" },
  manage_coupons: { label: "Gerenciar cupons",           description: "Cria e gerencia cupons e promoções" },
  send_messages:  { label: "Enviar mensagens",           description: "Envia mensagens e comunicados para participantes" },
};

export function deriveRoleFromPermissions(perms: OrgPermission[]): OrganizationRole {
  if (perms.includes("manage_team") || perms.includes("view_financial")) return "admin";
  if (perms.includes("checkin") && !perms.includes("edit_events")) return "staff";
  if (perms.includes("edit_events")) return "editor";
  return "staff";
}
export type MemberStatus = "pending" | "active" | "inactive";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  banner_url: string | null;
  website: string | null;
  social_links: Record<string, string> | null;
  verified: boolean;
  document_number: string | null;
  document_type: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  owner_id: string;
  settings: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string | null;
  email: string;
  role: OrganizationRole;
  status: MemberStatus;
  invited_by: string;
  invite_token: string;
  expires_at: string | null;
  created_at: string;
  accepted_at: string | null;
  custom_permissions: OrgPermission[] | null;
  profile?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    email: string | null;
  };
}

export interface UserOrganization {
  org_id: string;
  org_name: string;
  org_slug: string;
  org_logo: string | null;
  org_role: string;
  member_status: string;
  joined_at: string;
  custom_permissions: OrgPermission[] | null;
  invite_token?: string | null;
  invited_by_name?: string | null;
}

export async function getMyOrganizations(): Promise<UserOrganization[]> {
  const user = (await supabase.auth.getUser()).data.user;
  if (!user) return [];
  const { data, error } = await (supabase as any)
    .rpc("get_user_organizations", { p_user_id: user.id });
  if (error) throw error;
  return (data || []) as UserOrganization[];
}

export async function getPostableOrganizations(): Promise<UserOrganization[]> {
  const user = (await supabase.auth.getUser()).data.user;
  if (!user) return [];
  const { data, error } = await (supabase as any)
    .rpc("get_user_postable_organizations", { p_user_id: user.id });
  if (error) throw error;
  return ((data || []) as any[]).map((row: any) => ({
    org_id: row.org_id,
    org_name: row.org_name,
    org_slug: row.org_slug,
    org_logo: row.org_logo,
    org_role: row.role,
    member_status: "active",
    joined_at: "",
    custom_permissions: row.custom_permissions ?? null,
  }));
}

export async function getOrganizationBySlug(slug: string): Promise<Organization | null> {
  if (!slug) return null;
  const cleanSlug = slug.trim();
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[0-9a-f]{12}$/i.test(cleanSlug);

  let query = supabase.from("organizations").select("*");
  if (isUuid) {
    query = query.eq("id", cleanSlug);
  } else {
    query = query.ilike("slug", cleanSlug);
  }

  const { data, error } = await query.maybeSingle();
  if (error) {
    console.error("Erro ao buscar organização por slug:", error);
    return null;
  }
  return data as Organization | null;
}

export async function getOrganizationById(id: string): Promise<Organization | null> {
  const { data, error } = await supabase
    .from("organizations")
    .select("*")
    .eq("id", id)
    .single();
  if (error && error.code !== "PGRST116") throw error;
  return data as Organization | null;
}

export async function getOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
  const { data, error } = await supabase
    .from("organization_members")
    .select(`
      *,
      profile:profiles!organization_members_user_id_fkey(id, full_name, avatar_url, email)
    `)
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map((row: any) => ({
    ...row,
    profile: row.profile?.[0] || row.profile,
  }));
}

export async function createOrganization(params: {
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
}): Promise<string> {
  const { data, error } = await (supabase as any)
    .rpc("create_organization", {
      p_name: params.name,
      p_slug: params.slug,
      p_description: params.description || null,
      p_logo_url: params.logo_url || null,
    });
  if (error) throw error;
  return data as string;
}

export async function updateOrganization(
  id: string,
  updates: Partial<Omit<Organization, "id" | "owner_id" | "created_at" | "updated_at">>
): Promise<void> {
  const { error } = await supabase
    .from("organizations")
    .update(updates as any)
    .eq("id", id);
  if (error) throw error;
}

export async function deleteOrganization(id: string): Promise<void> {
  const user = (await supabase.auth.getUser()).data.user;
  if (!user) throw new Error("Usuário não autenticado.");

  // 1. Transfer all events of this organization to owner's personal account (organization_id = null)
  const { error: transferError } = await supabase
    .from("events")
    .update({ organization_id: null })
    .eq("organization_id", id);

  if (transferError) throw transferError;

  // 2. Remove all members and invitations associated with this organization
  const { error: membersError } = await supabase
    .from("organization_members")
    .delete()
    .eq("organization_id", id);

  if (membersError) throw membersError;

  // 3. Delete the organization
  const { error: deleteError } = await supabase
    .from("organizations")
    .delete()
    .eq("id", id);

  if (deleteError) throw deleteError;
}

export async function inviteOrganizationMember(
  organizationId: string,
  email: string,
  role: OrganizationRole = "editor",
  permissions?: OrgPermission[]
): Promise<{ success: boolean; token?: string; error?: string; emailSent?: boolean }> {
  const derivedRole = permissions && permissions.length > 0
    ? deriveRoleFromPermissions(permissions)
    : role;

  // Permissions are persisted atomically by the RPC (avoids email-casing races).
  const { data, error } = await (supabase as any)
    .rpc("invite_organization_member", {
      p_org_id: organizationId,
      p_email: email,
      p_role: derivedRole,
      p_permissions: permissions && permissions.length > 0 ? permissions : null,
    });
  if (error) throw error;

  const result = data as { success: boolean; token?: string; error?: string; emailSent?: boolean };

  if (result.success && result.token) {
    try {
      const { data: org } = await supabase
        .from("organizations")
        .select("name")
        .eq("id", organizationId)
        .single();

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", (await supabase.auth.getUser()).data.user?.id || "")
        .single();

      const { data: mailData, error: mailError } = await supabase.functions.invoke("send-org-invite", {
        body: {
          to: email,
          orgName: org?.name || "Organização",
          invitedByName: profile?.full_name || "",
          token: result.token,
          role: derivedRole,
        },
      });
      // Não mascarar falha: o caller decide se mostra o link como fallback.
      result.emailSent = !mailError && mailData?.success === true && typeof mailData.id === "string" && mailData.id.length > 0;
      if (!result.emailSent) {
        console.warn("[inviteOrganizationMember] envio não confirmado:", mailError?.name || "invalid_email_response", mailError?.message || "Resposta sem confirmação do provedor");
      }
    } catch (e: any) {
      console.warn("[inviteOrganizationMember] falha ao enviar e-mail:", e.message);
      result.emailSent = false;
    }
  }

  return result;
}

export async function resendOrganizationInvite(member: {
  email: string;
  invite_token: string;
  role: OrganizationRole;
  organization_id: string;
}): Promise<{ success: boolean; emailSent: boolean; link: string }> {
  const link = `${typeof window !== "undefined" ? window.location.origin : ""}/convite-org?token=${member.invite_token}`;
  let emailSent = false;
  try {
    const { data: org } = await supabase
      .from("organizations").select("name").eq("id", member.organization_id).single();
    const { data: profile } = await supabase
      .from("profiles").select("full_name")
      .eq("id", (await supabase.auth.getUser()).data.user?.id || "").single();
    const { data: mailData, error: mailError } = await supabase.functions.invoke("send-org-invite", {
      body: {
        to: member.email,
        orgName: org?.name || "Organização",
        invitedByName: profile?.full_name || "",
        token: member.invite_token,
        role: member.role,
      },
    });
    emailSent = !mailError && mailData?.success === true && typeof mailData.id === "string" && mailData.id.length > 0;
    if (!emailSent) {
      console.warn("[resendOrganizationInvite] envio não confirmado:", mailError?.name || "invalid_email_response", mailError?.message || "Resposta sem confirmação do provedor");
    }
  } catch (e: any) {
    console.warn("[resendOrganizationInvite] falha ao reenviar e-mail:", e.message);
  }
  return { success: true, emailSent, link };
}

export async function acceptOrganizationInvite(token: string): Promise<{
  success: boolean;
  error?: string;
  already_member?: boolean;
  organization_id?: string;
  organization_name?: string;
  role?: string;
}> {
  const { data, error } = await (supabase as any)
    .rpc("accept_organization_invite", { p_token: token });
  if (error) throw error;
  return data as any;
}

export async function declineOrganizationInvite(token: string): Promise<{
  success: boolean;
  error?: string;
}> {
  const { data, error } = await (supabase as any)
    .rpc("decline_organization_invite", { p_token: token });
  if (error) throw error;
  return data as any;
}

export async function removeOrganizationMember(memberId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  const { data, error } = await (supabase as any)
    .rpc("remove_organization_member", { p_member_id: memberId });
  if (error) throw error;
  return data as any;
}

export async function updateMemberRole(
  memberId: string,
  newRole: OrganizationRole
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await (supabase as any)
    .rpc("update_organization_member_role", {
      p_member_id: memberId,
      p_new_role: newRole,
    });
  if (error) throw error;
  return data as any;
}

export async function setMemberPermissions(
  memberId: string,
  permissions: OrgPermission[]
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await (supabase as any)
    .rpc("set_member_permissions", {
      p_member_id: memberId,
      p_permissions: permissions,
      p_role: deriveRoleFromPermissions(permissions),
    });
  if (error) throw error;
  return data as any;
}

export async function transferOrganizationOwnership(
  organizationId: string,
  newOwnerId: string
): Promise<{ success: boolean; error?: string; new_owner_id?: string }> {
  const { data, error } = await (supabase as any)
    .rpc("transfer_organization_ownership", {
      p_org_id: organizationId,
      p_new_owner_id: newOwnerId,
    });
  if (error) throw error;
  return data as any;
}

export async function getOrganizationEvents(organizationId: string) {
  const { data, error } = await (supabase as any)
    .from("events")
    .select("*, ticket_tiers(id, name, price, quantity_total, quantity_sold, quantity_reserved)")
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getOrganizationsEvents(organizationIds: string[]) {
  if (!organizationIds || organizationIds.length === 0) return [];
  const { data, error } = await (supabase as any)
    .from("events")
    .select("*, ticket_tiers(id, name, price, quantity_total, quantity_sold, quantity_reserved)")
    .in("organization_id", organizationIds)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getPendingInvites(): Promise<OrganizationMember[]> {
  const user = (await supabase.auth.getUser()).data.user;
  if (!user) return [];
  const { data, error } = await supabase
    .from("organization_members")
    .select(`
      *,
      organization:organizations(id, name, slug, logo_url)
    `)
    .eq("status", "pending")
    .or(`user_id.eq.${user.id},email.ilike.${user.email || ""}`);
  if (error) throw error;
  return (data || []).map((row: any) => ({
    ...row,
    organization: row.organization?.[0] || row.organization,
  }));
}

export function canManageMembers(role: OrganizationRole): boolean {
  return role === "owner" || role === "admin";
}

export function canPostEvents(role: OrganizationRole): boolean {
  return ["owner", "admin", "editor", "manager"].includes(role);
}

export function canDeleteEvents(role: OrganizationRole): boolean {
  return role === "owner" || role === "admin";
}

export function canEditOrgSettings(role: OrganizationRole): boolean {
  return role === "owner" || role === "admin";
}

export function canChangeRoles(role: OrganizationRole): boolean {
  return role === "owner" || role === "admin";
}

export function canTransferOwnership(role: OrganizationRole): boolean {
  return role === "owner";
}

export function canRefundOrders(role: OrganizationRole): boolean {
  return ["owner", "admin", "finance", "manager"].includes(role);
}

export function canManageFinancial(role: OrganizationRole): boolean {
  return ["owner", "admin", "finance"].includes(role);
}

export function canWithdraw(role: OrganizationRole): boolean {
  return ["owner", "admin", "finance"].includes(role);
}

export function canManageBankAccounts(role: OrganizationRole): boolean {
  return ["owner", "admin", "finance"].includes(role);
}

export function canCancelPaidTickets(role: OrganizationRole): boolean {
  return ["owner", "admin", "finance", "manager"].includes(role);
}

export function canCancelFreeTickets(role: OrganizationRole): boolean {
  return ["owner", "admin", "manager", "editor", "finance"].includes(role);
}

export const roleLabels: Record<OrganizationRole, string> = {
  owner:         "Proprietário",
  admin:         "Administrador",
  editor:        "Editor",
  staff:         "Staff",
  finance:       "Financeiro",
  manager:       "Gerente",
  viewer:        "Visualizador",
  checkin_staff: "Staff Check-in",
  reports_only:  "Apenas Relatórios",
};

export const roleDescriptions: Record<OrganizationRole, string> = {
  owner:         "Controle total da organização, incluindo exclusão e transferência de propriedade.",
  admin:         "Gerencia membros, financeiro, configurações e todos os eventos da organização.",
  editor:        "Cria, edita e publica eventos, gerencia ingressos, cupons e participantes.",
  staff:         "Acesso somente ao check-in. Convidado por evento via link, sem acesso ao painel de gestão.",
  finance:       "Acesso total à gestão financeira, relatórios, saques e estornos de pedidos.",
  manager:       "Gerencia operações do evento, pedidos, participantes e equipe.",
  viewer:        "Acesso somente para visualização de dados básicos do evento.",
  checkin_staff: "Acesso operacional exclusivo para leitura de ingressos e check-in.",
  reports_only:  "Acesso focado na emissão e análise de relatórios do evento.",
};

export const inviteableRoles: OrganizationRole[] = [
  "admin",
  "editor",
  "finance",
  "manager",
  "staff",
];

export interface OrganizationRoleRequest {
  id: string;
  organization_id: string;
  user_id: string;
  current_role: OrganizationRole;
  requested_role: OrganizationRole;
  reason: string | null;
  status: "pending" | "approved" | "rejected";
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  profile?: {
    full_name: string | null;
    avatar_url: string | null;
    email: string | null;
  };
}

export async function submitOrganizationRoleRequest(
  organizationId: string,
  requestedRole: OrganizationRole,
  reason?: string
): Promise<{ success: boolean; error?: string; request_id?: string }> {
  const { data, error } = await (supabase as any).rpc("submit_organization_role_request", {
    p_org_id: organizationId,
    p_requested_role: requestedRole,
    p_reason: reason || null,
  });
  if (error) return { success: false, error: error.message };
  return data as any;
}

export async function getOrganizationRoleRequests(organizationId: string): Promise<OrganizationRoleRequest[]> {
  const { data, error } = await (supabase as any)
    .from("organization_role_requests")
    .select(`
      *,
      profile:profiles!user_id(full_name, avatar_url, email)
    `)
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []) as OrganizationRoleRequest[];
}

export async function reviewOrganizationRoleRequest(
  requestId: string,
  action: "approved" | "rejected"
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await (supabase as any).rpc("review_organization_role_request", {
    p_request_id: requestId,
    p_action: action,
  });
  if (error) return { success: false, error: error.message };
  return data as any;
}

